import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { useGetAlbumVideosInfinite } from '@vimeo/gctl-api/hooks/useAlbumVideos';
import { getInfiniteRequestLoadingState } from '@vimeo/ui/libs/getInfiniteRequestLoadingState';
import { useViewer } from '@vimeo/viewer';
import { useBaseUrl } from '@vimeo/viewer-showcase-ui/hooks/use-base-url';
import { getEventIdFromClip } from '@vimeo/viewer-showcase-ui/utils/helper';

import { useAlbumStore } from '../state/albumStore';
import { VIDEOS_API_VERSION } from '../utils/constants';
import { contextUrlParser } from '../utils/helper';

/**
 * Performs an infinite search on the current showcase given text input
 */
export const useShowcaseSearch = (text: string) => {
  const router = useRouter();
  const baseUrl = useBaseUrl();
  const albumId = useAlbumStore((state) => state.albumId);
  const password = useAlbumStore((state) => state.hashedPass);
  const viewer = useViewer();
  const isShowcaseEmbedded = useAlbumStore((state) => state.isShowcaseEmbedded);
  const referrer = useAlbumStore((state) => state.referrer);

  const ITEMS_PER_PAGE = 15;
  const DEFAULT_QUERY = {
    forceCollectionUri: true,
    sizes: ['640x360', '360x203'],
    perPage: ITEMS_PER_PAGE,
  };

  const {
    data: albumVideos,
    isLoading,
    error,
    size,
    setSize,
    ...gctlConfig
  } = useGetAlbumVideosInfinite(() => {
    if (!viewer || !text) return null;

    return {
      where: {
        albumId,
      },
      select: [
        'name',
        'duration',
        'uri',
        'link',
        'live',
        'pictures.sizes.link',
        'pictures.sizes.width',
        'pictures.uri',
        'user.link',
        'user.name',
        'user.pictures.sizes.link',
        'user.pictures.sizes.width',
        'user.uri',
      ],
      query: {
        ...DEFAULT_QUERY,
        query: text,
        sort: 'alphabetical',
        direction: 'asc',
        ...(password && { password }),
        isEmbed: isShowcaseEmbedded,
        referrer: referrer,
      },
      headers: { Accept: `application/vnd.vimeo.*+json;version=${VIDEOS_API_VERSION}` },
    };
  });

  const { isLoadingMore, isDone } = getInfiniteRequestLoadingState({
    data: albumVideos,
    size,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const results = useMemo(
    () =>
      albumVideos
        ?.filter(Boolean)
        ?.flatMap((video) => video.data)
        ?.map((clip) => {
          const eventId = getEventIdFromClip(clip); // live clip finished archive (transcoding) process
          const { link } = contextUrlParser(clip.link, { baseUrl, eventId });

          return {
            onClick: () => {
              router.push(link);
            },
            duration: clip.duration,
            user: clip.user,
            name: clip.name,
            src: clip.pictures?.sizes?.[0]?.link,
            srcSet: clip.pictures?.sizes?.map((size) => `${size.link} ${size.width}w`).join(', '),
            alt: `"${clip.name}" thumbnail`,
          };
        }),
    [albumVideos, baseUrl, router],
  );

  const loadMoreResults = useCallback(() => {
    setSize(size + 1);
  }, [size, setSize]);

  return {
    canLoadMore: !isDone && !isLoading,
    isLoading,
    isLoadingMore,
    loadMoreResults,
    error,
    results,
    ...gctlConfig,
  };
};
